import { assertUnreachable } from "@aprao/common/src/assertUnreachable.js";
import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import { opposite } from "@aprao/common/src/math/opposite.js";
import { sum } from "@aprao/common/src/math/sum.js";
import { makeAutoObservable } from "mobx";
import { computedFn } from "mobx-utils";

import type {
  AppraisalDocumentFacade,
  BuildStageFacade,
  LendingStageFacade,
  OtherCostsStageFacade,
  PurchaseStageFacade,
  RevenueStageFacade,
} from "../AppraisalDocumentFacade.js";
import type { BuildCostsPreliminaryFacade } from "../build/BuildCostsPreliminaryFacade.js";
import type { BuildPhaseFacade } from "../build/BuildPhaseFacade.js";
import type { FinanceFacade } from "../FinanceFacade.js";
import type { AnyLenderFacade } from "../lender/AnyLenderFacade.js";
import type { LenderFeeFacade } from "../lender/LenderBaseFacade.js";
import type { OtherCostFacade } from "../OtherCostsGroupFacade.js";
import type { ResidualFacade } from "../ResidualFacade.js";
import type { OperatedAssetGroupFacade } from "../revenue/OperatedAssetGroupFacade.js";
import type { OtherIncomeGroupFacade } from "../revenue/OtherIncomeGroupFacade.js";
import type { ResidentialSalesGroupFacade } from "../revenue/ResidentialSalesGroupFacade.js";
import type { SitePriceManager } from "../SitePriceManager.js";
import type {
  CustomCostFacade,
  SitePurchaseFacade,
} from "../SitePurchaseFacade.js";
import { type FrameRange, spanForRange } from "./FrameRange.js";
import {
  type CashflowAmount,
  TimelineOverrideFacade,
  type TimelineItemAdapter,
  TimelineWritableOverrideFacade,
  TimelineStageAdapter,
} from "./TimelineStageAdapter.js";

export class TimelineFacade {
  $stores;

  constructor(stores: {
    documentFacade: AppraisalDocumentFacade;
    residualFacade: ResidualFacade;
    priceManager: SitePriceManager;
    financeFacade: FinanceFacade;
  }) {
    this.$stores = stores;
  }

  getRevenueValueGrossItemAdapter = computedFn(
    (
      group: OperatedAssetGroupFacade | OtherIncomeGroupFacade,
    ): TimelineItemAdapter<TimelineWritableOverrideFacade> =>
      makeAutoObservable({
        get currencyAmount() {
          return group.valueGross;
        },
        get timelineOverride() {
          return new TimelineWritableOverrideFacade(
            group.$model.timelineOverride,
          );
        },
      }),
  );

  getResidentialBalanceItemAdapter = computedFn(
    (
      group: ResidentialSalesGroupFacade,
    ): TimelineItemAdapter<TimelineWritableOverrideFacade> =>
      makeAutoObservable({
        get currencyAmount() {
          return sum([
            group.valueGross,
            opposite(group.deposit.relativesSet.getRelative("absolute")),
          ]);
        },
        get timelineOverride() {
          return new TimelineWritableOverrideFacade(
            group.$model.balanceTimelineOverride,
          );
        },
      }),
  );

  getResidentialDepositItemAdapter = computedFn(
    (
      group: ResidentialSalesGroupFacade,
    ): TimelineItemAdapter<TimelineWritableOverrideFacade> =>
      makeAutoObservable({
        get currencyAmount() {
          return group.deposit.relativesSet.getRelative("absolute");
        },
        get timelineOverride() {
          return new TimelineWritableOverrideFacade(
            group.$model.depositTimelineOverride,
          );
        },
      }),
  );

  getSalesCostsItemAdapter = computedFn(
    (
      group: ResidentialSalesGroupFacade | OperatedAssetGroupFacade,
    ): TimelineItemAdapter =>
      makeAutoObservable({
        get currencyAmount() {
          return opposite(group.costsTotal);
        },
        get timelineOverride() {
          // The sales costs' timeline is not editable but matches the completion
          // of sale's timeline.
          return new TimelineOverrideFacade(
            "balanceTimelineOverride" in group.$model ?
              group.$model.balanceTimelineOverride
            : group.$model.timelineOverride,
          );
        },
      }),
  );

  getRevenueStageAdapter = computedFn(
    (stage: RevenueStageFacade) =>
      new TimelineStageAdapter(stage.$model, () =>
        stage.revenueGroups.flatMap((group) =>
          group.category === "residential" ?
            [
              this.getResidentialBalanceItemAdapter(group),
              this.getResidentialDepositItemAdapter(group),
              this.getSalesCostsItemAdapter(group),
            ]
          : group.category === "operated" ?
            [
              this.getRevenueValueGrossItemAdapter(group),
              this.getSalesCostsItemAdapter(group),
            ]
          : group.category === "other" ?
            [this.getRevenueValueGrossItemAdapter(group)]
          : assertUnreachable(group),
        ),
      ),
  );

  getPurchasePriceItemAdapter = computedFn(
    (
      site: SitePurchaseFacade,
    ): TimelineItemAdapter<TimelineWritableOverrideFacade> => {
      const priceManager = this.$stores.priceManager;

      return makeAutoObservable({
        get currencyAmount() {
          return priceManager.getPriceBySite(site);
        },
        get timelineOverride() {
          return new TimelineWritableOverrideFacade(
            site.$model.pricePurchase.timelineOverride,
          );
        },
      });
    },
  );

  getLandTaxItemAdapter = computedFn(
    (
      site: SitePurchaseFacade,
    ): TimelineItemAdapter<TimelineWritableOverrideFacade> => {
      const residualFacade = this.$stores.residualFacade;

      return makeAutoObservable({
        get currencyAmount() {
          return residualFacade.getLandTaxBySite(site).landTax;
        },
        get timelineOverride() {
          return new TimelineWritableOverrideFacade(
            site.$model.landTax.timelineOverride,
          );
        },
      });
    },
  );

  getPurchaseCostItemAdapter = computedFn(
    (
      cost: CustomCostFacade,
    ): TimelineItemAdapter<TimelineWritableOverrideFacade> =>
      makeAutoObservable({
        get currencyAmount() {
          return cost.relativesSet.getRelative("absolute");
        },
        get timelineOverride() {
          return new TimelineWritableOverrideFacade(
            cost.$model.timelineOverride,
          );
        },
      }),
  );

  getPurchaseStageAdapter = computedFn((stage: PurchaseStageFacade) => {
    const residualFacade = this.$stores.residualFacade;

    return new TimelineStageAdapter(stage.$model, () =>
      stage.sitePurchases.flatMap((site) => [
        this.getPurchasePriceItemAdapter(site),
        this.getLandTaxItemAdapter(site),
        ...residualFacade
          .getCustomCostsBySite(site)
          .map((cost) => this.getPurchaseCostItemAdapter(cost)),
      ]),
    );
  });

  getBuildPreliminaryItemAdapter = computedFn(
    (
      preliminary: BuildCostsPreliminaryFacade,
    ): TimelineItemAdapter<TimelineWritableOverrideFacade> =>
      makeAutoObservable({
        get currencyAmount() {
          return preliminary.totalCost;
        },
        get timelineOverride() {
          return new TimelineWritableOverrideFacade(
            preliminary.$model.timelineOverride,
          );
        },
      }),
  );

  getBuildPhaseItemAdapter = computedFn(
    (
      phase: BuildPhaseFacade,
    ): TimelineItemAdapter<TimelineWritableOverrideFacade> =>
      makeAutoObservable({
        get currencyAmount() {
          return phase.costTotalWithContingency;
        },
        get timelineOverride() {
          return new TimelineWritableOverrideFacade(
            phase.$model.timelineOverride,
          );
        },
      }),
  );

  getBuildStageAdapter = computedFn(
    (stage: BuildStageFacade) =>
      new TimelineStageAdapter(stage.$model, () => [
        this.getBuildPreliminaryItemAdapter(stage.preliminary),
        ...stage.buildPhases.map((phase) =>
          this.getBuildPhaseItemAdapter(phase),
        ),
      ]),
  );

  getLenderFeeItemAdapter = computedFn(
    (
      fee: LenderFeeFacade,
      lender: AnyLenderFacade,
    ): TimelineItemAdapter<TimelineWritableOverrideFacade> => {
      const financeFacade = this.$stores.financeFacade;

      return makeAutoObservable({
        get currencyAmount() {
          return financeFacade
            .getLenderFeeRelativesDelegateByStore(fee, lender)
            .getRelative("absolute");
        },
        get timelineOverride() {
          return new TimelineWritableOverrideFacade(
            fee.$model.timelineOverride,
          );
        },
      });
    },
  );

  getLendingStageAdapter = computedFn((stage: LendingStageFacade) => {
    const residualFacade = this.$stores.residualFacade;

    return new TimelineStageAdapter(stage.$model, () =>
      stage.$model.lenders.flatMap((lender) => {
        const lenderFacade = residualFacade.getLenderFacade(lender);

        return lenderFacade.fees.map((fee) =>
          this.getLenderFeeItemAdapter(fee, lenderFacade),
        );
      }),
    );
  });

  getOtherCostItemAdapter = computedFn(
    (
      cost: OtherCostFacade,
    ): TimelineItemAdapter<TimelineWritableOverrideFacade> =>
      makeAutoObservable({
        get currencyAmount() {
          return cost.relativesSet.getRelative("absolute");
        },
        get timelineOverride() {
          return new TimelineWritableOverrideFacade(
            cost.$model.timelineOverride,
          );
        },
      }),
  );

  getOtherCostsStageAdapter = computedFn(
    (stage: OtherCostsStageFacade) =>
      new TimelineStageAdapter(stage.$model, () =>
        stage.otherCostsGroups.flatMap((group) =>
          group.costs.map((cost) => this.getOtherCostItemAdapter(cost)),
        ),
      ),
  );

  get computedFrameRange() {
    const allStages = [
      this.getRevenueStageAdapter(this.$stores.documentFacade.stages.revenue),
      this.getPurchaseStageAdapter(this.$stores.documentFacade.stages.purchase),
      this.getBuildStageAdapter(this.$stores.documentFacade.stages.build),
      this.getOtherCostsStageAdapter(
        this.$stores.documentFacade.stages.otherCosts,
      ),
      this.getLendingStageAdapter(this.$stores.documentFacade.stages.lending),
    ];

    let range: FrameRange = { lower: null, upper: null };
    for (const stage of allStages) {
      range = spanForRange(range, stage.computedFrameRange);
    }

    return range;
  }

  getNetProceedsCashflowAtFrame = (frameIndex: number): CashflowAmount => {
    const _sum = {
      amount: FRACTION_ZERO,
    };

    for (const stage of [
      this.getRevenueStageAdapter(this.$stores.documentFacade.stages.revenue),
    ]) {
      const inflow = stage.getStageCashflowAtFrame(frameIndex, "inflow");
      const outflow = stage.getStageCashflowAtFrame(frameIndex, "outflow");

      _sum.amount = _sum.amount.add(inflow.amount).add(outflow.amount);
    }

    return _sum;
  };

  getTotalCostsCashflowAtFrame = (frameIndex: number): CashflowAmount => {
    const _sum = {
      amount: FRACTION_ZERO,
    };

    for (const stage of [
      this.getBuildStageAdapter(this.$stores.documentFacade.stages.build),
      this.getPurchaseStageAdapter(this.$stores.documentFacade.stages.purchase),
      this.getOtherCostsStageAdapter(
        this.$stores.documentFacade.stages.otherCosts,
      ),
    ]) {
      const cashflow = stage.getStageCashflowAtFrame(frameIndex, "inflow");
      _sum.amount = _sum.amount.add(cashflow.amount);
    }

    return _sum;
  };

  getNetCashflowAtFrame = (frameIndex: number): CashflowAmount => {
    const revenue = this.getNetProceedsCashflowAtFrame(frameIndex);
    const totalCost = this.getTotalCostsCashflowAtFrame(frameIndex);

    return {
      amount: revenue.amount.sub(totalCost.amount),
    };
  };

  get netProceedsAggregatedCashflow(): CashflowAmount {
    const _sum = {
      amount: FRACTION_ZERO,
    };

    for (const stage of [
      this.getRevenueStageAdapter(this.$stores.documentFacade.stages.revenue),
    ]) {
      const inflow = stage.getStageAggregatedCashflow("inflow");
      const outflow = stage.getStageAggregatedCashflow("outflow");
      _sum.amount = _sum.amount.add(inflow.amount).add(outflow.amount);
    }

    return _sum;
  }

  get netBalances() {
    let _sum = FRACTION_ZERO;

    const balances = [];

    for (
      let frameIndex = 0;
      frameIndex < (this.computedFrameRange.upper ?? 0);
      frameIndex++
    ) {
      const netCashflow = this.getNetCashflowAtFrame(frameIndex);

      balances.push((_sum = _sum.add(netCashflow.amount)));
    }

    return balances;
  }

  get totalCostsAggregatedCashflow(): CashflowAmount {
    const _sum = {
      amount: FRACTION_ZERO,
    };

    for (const stage of [
      this.getBuildStageAdapter(this.$stores.documentFacade.stages.build),
      this.getPurchaseStageAdapter(this.$stores.documentFacade.stages.purchase),
      this.getOtherCostsStageAdapter(
        this.$stores.documentFacade.stages.otherCosts,
      ),
    ]) {
      _sum.amount = _sum.amount.add(
        stage.getStageAggregatedCashflow("inflow").amount,
      );
    }

    return _sum;
  }

  get netAggregatedCashflow(): CashflowAmount {
    return {
      amount: this.netProceedsAggregatedCashflow.amount.sub(
        this.totalCostsAggregatedCashflow.amount,
      ),
    };
  }
}
